import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Office } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupOfficesProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const officeTypeOptions = [
  { value: 'headquarters', label: 'Headquarters' },
  { value: 'regional', label: 'Regional' },
  { value: 'satellite', label: 'Satellite' },
  { value: 'virtual', label: 'Virtual' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'closed', label: 'Closed' },
];

const SetupOffices: React.FC<SetupOfficesProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [offices, setOffices] = useState<Office[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<Office>>({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchOffices = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const data = await get<{ data: Office[] }>(`/api/admin/setup/offices?${params}`);
      setOffices(data.data || []);
    } catch (error) {
      console.error('Failed to fetch offices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchOffices();
  }, [fetchOffices]);

  const handleRowClick = (office: Office) => {
    setSelectedOffice(office);
    setFormData(office);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedOffice(null);
    setFormData({
      code: '',
      name: '',
      officeType: 'satellite',
      status: 'active',
      address: {
        street1: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
    });
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street1: prev.address?.street1 || '',
          city: prev.address?.city || '',
          state: prev.address?.state || '',
          zip: prev.address?.zip || '',
          country: prev.address?.country || 'US',
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await post('/api/admin/setup/offices', formData);
      } else {
        await patch(`/api/admin/setup/offices/${selectedOffice?.id}`, formData);
      }

      setIsDrawerOpen(false);
      fetchOffices();
    } catch (error) {
      console.error('Failed to save office:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      key: 'code',
      header: 'Code',
      width: '120px',
      render: (office: Office) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: brand.colors.link }}>
            {office.code}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={office.name}
            fields={[
              { label: 'Code', value: office.code },
              { label: 'Type', value: office.officeType },
              { label: 'Location', value: office.address ? `${office.address.city}, ${office.address.state}` : '-' },
              { label: 'Phone', value: office.phone || '-' },
              { label: 'Timezone', value: office.timezone || '-' },
            ]}
          />
        </div>
      ),
    },
    { key: 'name', header: 'Name' },
    {
      key: 'officeType',
      header: 'Type',
      render: (office: Office) => (
        <span className="capitalize">{office.officeType}</span>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (office: Office) => (
        <span>
          {office.address.city}, {office.address.state}
        </span>
      ),
    },
    { key: 'phone', header: 'Phone' },
    {
      key: 'status',
      header: 'Status',
      render: (office: Office) => <StatusBadge status={office.status} />,
    },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search offices..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        onAdd={handleAdd}
        addLabel="Add Office"
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={offices}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No offices found"
        getRowId={(office) => office.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'Add Office' : 'Edit Office'}
        subtitle={selectedOffice?.code}
        brand={brand}
        Icons={Icons}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Code"
              name="code"
              value={formData.code || ''}
              onChange={handleInputChange}
              required
              placeholder="e.g., LA-HQ"
            />
            <FormSelect
              brand={brand}
              label="Type"
              name="officeType"
              value={formData.officeType || 'satellite'}
              onChange={handleInputChange}
              options={officeTypeOptions}
            />
          </div>

          <FormInput
            brand={brand}
            label="Name"
            name="name"
            value={formData.name || ''}
            onChange={handleInputChange}
            required
            placeholder="Office name"
          />

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Address
            </h3>
            <FormInput
              brand={brand}
              label="Street Address"
              name="address.street1"
              value={formData.address?.street1 || ''}
              onChange={handleInputChange}
              required
            />
            <FormInput
              brand={brand}
              label="Suite/Floor"
              name="address.street2"
              value={formData.address?.street2 || ''}
              onChange={handleInputChange}
            />
            <div className="grid grid-cols-3 gap-4">
              <FormInput
                brand={brand}
                label="City"
                name="address.city"
                value={formData.address?.city || ''}
                onChange={handleInputChange}
                required
              />
              <FormInput
                brand={brand}
                label="State"
                name="address.state"
                value={formData.address?.state || ''}
                onChange={handleInputChange}
                required
              />
              <FormInput
                brand={brand}
                label="ZIP"
                name="address.zip"
                value={formData.address?.zip || ''}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Contact
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Fax"
                name="fax"
                type="tel"
                value={formData.fax || ''}
                onChange={handleInputChange}
              />
            </div>
            <FormInput
              brand={brand}
              label="Email"
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={handleInputChange}
            />
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <FormSelect
              brand={brand}
              label="Status"
              name="status"
              value={formData.status || 'active'}
              onChange={handleInputChange}
              options={statusOptions}
            />
            <FormTextarea
              brand={brand}
              label="Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={3}
            />
          </div>
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupOffices;
